import React, { useState, useEffect, useCallback } from 'react';
import { Award, Clock, Users, Shield, DollarSign, Zap, ChevronLeft, ChevronRight } from 'lucide-react';
import CounterAnimation from './CounterAnimation';

const WhyChooseUs = () => {
  const [currentCard, setCurrentCard] = useState(0);
  const benefits = [
    {
      icon: Award,
      title: 'Approval Rate',
      statNum: 98,
      statSuffix: '%',
      description: 'Your trademark gets approved the first time - we deliver industry-leading success rates that save you time and money'
    },
    {
      icon: Clock,
      title: 'Lightning-Fast Filing',
      description: '24-48 hour application turnaround - start protecting your brand this week, not next month'
    },
    {
      icon: Users,
      title: 'Happy Clients',
      statNum: 10000,
      statSuffix: '+',
      description: 'Join 10,000+ successful businesses who chose us to protect their most valuable asset'
    },
    {
      icon: Shield,
      title: 'Risk-Free Guarantee',
      description: 'Zero risk to you - get a complete refund if your trademark is unavailable. We stand behind our work'
    },
    {
      icon: DollarSign,
      title: 'No Hidden Costs',
      description: 'What you see is what you pay - transparent pricing from $45 plus filing fees. No surprises, ever'
    },
    {
      icon: Zap,
      title: 'AI-Powered Search',
      description: 'Instant conflict detection - our advanced AI scans millions of trademarks in seconds, catching issues before they cost you'
    }
  ];

  const nextCard = useCallback(() => setCurrentCard(p => (p + 1) % benefits.length), [benefits.length]);

  useEffect(() => {
    const timer = setInterval(nextCard, 4000);
    return () => clearInterval(timer);
  }, [nextCard]);

  return (
    <div className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-[#404138] mb-4">
            Why Choose Norfolk Trademark?
          </h2>
          <p className="text-base text-[#6E6B6A] max-w-2xl mx-auto">
            Get your trademark approved faster with our proven system that combines AI technology and expert legal guidance
          </p>
        </div>

        {/* Desktop Grid */}
        <div className="hidden md:grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {benefits.map((benefit, index) => (
            <div
              key={index}
              className="bg-gray-50 rounded-xl p-6 hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 group hover:bg-gradient-to-br hover:from-[#FF035B] hover:to-[#2F1A8E] hover:scale-105"
            >
              <div className="w-14 h-14 bg-[#FF035B] group-hover:bg-white/20 rounded-lg flex items-center justify-center mb-4 transition-all duration-500 group-hover:scale-110 group-hover:rotate-6">
                <benefit.icon className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-xl font-bold text-black group-hover:text-white mb-3 transition-colors duration-500">
                {benefit.statNum ? (
                  <>
                    <CounterAnimation 
                      end={benefit.statNum} 
                      suffix={benefit.statSuffix || ''} 
                    />{' '}
                    {benefit.title}
                  </>
                ) : (
                  benefit.title
                )}
              </h3>
              <p className="text-gray-600 group-hover:text-white/90 transition-colors duration-500">
                {benefit.description}
              </p>
            </div>
          ))}
        </div>

        {/* Mobile Auto-Slider */}
        <div className="md:hidden">
          <div className="overflow-hidden">
            <div className="transition-transform duration-500 ease-in-out flex" style={{ transform: `translateX(-${currentCard * 100}%)` }}>
              {benefits.map((benefit, index) => (
                <div key={index} className="w-full flex-shrink-0 px-2">
                  <div className="bg-gray-50 rounded-xl p-6">
                    <div className="w-14 h-14 bg-gradient-to-br from-[#FF035B] to-[#2F1A8E] rounded-lg flex items-center justify-center mb-4">
                      <benefit.icon className="w-7 h-7 text-white" />
                    </div>
                    <h3 className="text-lg font-bold text-black mb-3">
                      {benefit.statNum ? (
                        <><CounterAnimation end={benefit.statNum} suffix={benefit.statSuffix || ''} /> {benefit.title}</>
                      ) : benefit.title}
                    </h3>
                    <p className="text-sm text-gray-600">{benefit.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="flex justify-center items-center gap-4 mt-4">
            <button onClick={() => setCurrentCard(p => (p - 1 + benefits.length) % benefits.length)} className="w-8 h-8 rounded-full bg-[#FF035B] text-white flex items-center justify-center"><ChevronLeft className="w-4 h-4" /></button>
            <div className="flex gap-2">
              {benefits.map((_, i) => (
                <button key={i} onClick={() => setCurrentCard(i)} className={`h-2 rounded-full transition-all ${currentCard === i ? 'bg-[#FF035B] w-6' : 'bg-gray-300 w-2'}`} />
              ))}
            </div>
            <button onClick={nextCard} className="w-8 h-8 rounded-full bg-[#FF035B] text-white flex items-center justify-center"><ChevronRight className="w-4 h-4" /></button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WhyChooseUs;